/**
 * useHeroVariant — explicit state-machine layer over the (engineState ×
 * hasData × activeMetric) tuple that the hero pill at
 * `SpeedtestResults.vue` switches on.
 *
 * **Why this composable exists** — per AC-r3 F2 audit § K8 the pre-W5d
 * hero v-if was a 4-branch cascade where ordering decided correctness.
 * The cascade evaluated `isStarted` BEFORE `hasData`, so once the engine
 * flipped a metric's state to `started` the dots branch claimed the
 * slot forever (or at least until `allComplete` flipped). The value
 * branch was unreachable during the running test — F2.I-01 BLOCKER.
 *
 * Rather than fix the ordering inline (a one-character swap that the
 * next contributor would re-break) the K8 re-route lifts the gating
 * into an explicit state machine. The variant is the answer; the v-if
 * chain at the consumer reads `isRunningWithData` / `isRunningNoData`
 * directly. Adding a future branch (e.g. `aborted-with-partial-data`)
 * is a discriminated-union addition here, with an exhaustiveness gate
 * the consumer's v-if respects via the typed readouts.
 *
 * **Absorbed F2 findings (K8 carve, per Revision-3-r-3 § 9 ratification)**:
 *   - F2.I-01 — the dots branch only fires when the active metric has
 *     no data yet (`isRunningNoData`); the value branch wins on the
 *     same paint as soon as the first sample lands.
 *   - F2.C-02 — the bottom phase-detail strip composes off the same
 *     state machine; consumers gate "same number twice" cases on
 *     `isRunningWithData`.
 *   - F2.C-04 — meter saturation reads the variant rather than peak
 *     amplitude; ring fill alpha tracks `isRunningNoData` vs
 *     `isRunningWithData` so the dial doesn't paint "complete" on the
 *     first sample.
 *   - F2.I-03 — the disabled-back-button affordance gates on
 *     `isIdle`; the dock retires the dim disabled state at idle.
 *   - F2.I-05 — the tabindex reordering at idle reads `isIdle` so the
 *     timeline markers are not-tabbable until the active state lands.
 *
 * **State-machine shape** — five mutually exclusive variants exhaust
 * the (engineState × hasData × activeMetric) space:
 *
 *   variant            | engineState | activeMetric | hasData | meaning
 *   -------------------|-------------|--------------|---------|--------
 *   idle               | idle        | n/a          | n/a     | pre-engine; idle pill paints "0 ms"
 *   running-no-data    | running     | non-null     | false   | sampling started, no value yet → dots
 *   running-with-data  | running     | non-null     | true    | live value flowing → hero pill
 *   complete           | complete    | n/a          | n/a     | every visible metric finished → complete-wrap
 *   aborted            | idle        | n/a          | n/a     | post-running drop without complete; surfaces as idle
 *
 * The `aborted` variant is structurally distinct from `idle` even
 * though both project to `engineState === "idle"` — `aborted` carries
 * the "last run did not complete" signal so consumers can surface a
 * retry CTA or fade the meter to its idle posture cleanly. The
 * subscription machine's `abortedByUser` latch is the source.
 *
 * **Triumvirate constraint** — six F2 findings absorbed (the five
 * named above plus the surface F2.C-01 complete-state composition,
 * which keys on `isComplete`). The composable is sealed at this
 * scope; a seventh sibling pattern surfacing is a HALT-for-user
 * signal per the W5d spec triumvirate.
 *
 * **No side effects** — the composable returns computed refs only;
 * the rAF subscription, formatter, smoother, and store reads live in
 * `useMetricResults()` + `useMetricStateMachine()`. This composable
 * is a pure projection.
 */

import { computed, type ComputedRef } from "vue";

import {
    useEngineState,
    type EngineState,
} from "./useEngineState.ts";
import { useMetricRegistry } from "./useMetricRegistry.ts";
import {
    useMetricResults,
    type UseMetricResult,
} from "./useMetricResult.ts";
import { useMetricStateMachine } from "./useMetricStateMachine.ts";
import { TestState } from "@features/speedtest/state/index.ts";
import type { MetricDef } from "@src/types/speedtest.ts";

/** Discriminated state-machine output. The five variants exhaust the
 *  (engineState × hasData) cross-product the hero pill switches on. */
export type HeroVariant =
    | "idle"
    | "running-no-data"
    | "running-with-data"
    | "complete"
    | "aborted";

export interface UseHeroVariant {
    /** The canonical variant — single source of truth for hero v-if. */
    variant: ComputedRef<HeroVariant>;
    /** The engine state this variant projects from. */
    engineState: ComputedRef<EngineState>;
    /** Active metric (the one currently running, or the last one when complete);
     *  `null` at idle / aborted when nothing is in flight. */
    activeMetric: ComputedRef<MetricDef | null>;
    /** Idle-paint metric — pre-engine placeholder reads this so the LCP
     *  candidate stamps the first visible metric. */
    idlePlaceholder: ComputedRef<MetricDef | null>;
    /** Per-metric bundle map (the same map `useMetricResults` produces).
     *  Returned here so the consumer doesn't double-instantiate. */
    results: Record<MetricDef["id"], UseMetricResult>;
    /** Discriminated readouts — exactly one is true at any moment. */
    isIdle: ComputedRef<boolean>;
    isRunningNoData: ComputedRef<boolean>;
    isRunningWithData: ComputedRef<boolean>;
    isComplete: ComputedRef<boolean>;
    isAborted: ComputedRef<boolean>;
}

/**
 * Compose the hero state machine.
 *
 * The composable instantiates one `useMetricResults()` map (which owns
 * the rAF smoother fan-out). Callers must use the returned `results`
 * map rather than instantiating their own — `useMetricResults()` is
 * setup-time, and double-instantiation would fan out a second smoother
 * graph. The exposed `results` field exists for exactly this reason.
 */
export function useHeroVariant(): UseHeroVariant {
    const engineState = useEngineState();
    const registry = useMetricRegistry();
    const stateMachine = useMetricStateMachine();
    const results = useMetricResults();

    /** The active metric — the first visible metric whose state is
     *  `started` OR `active`. Falls back to the last visible metric
     *  when every metric is finished (the complete state still wants
     *  to identify which metric "owns" the last value paint). */
    const activeMetric = computed<MetricDef | null>(() => {
        const visible = registry.visible.value;
        const live = visible.find((m) => results[m.id].isActiveOrStarted.value);
        if (live) return live;
        if (stateMachine.allComplete.value) {
            return visible[visible.length - 1] ?? null;
        }
        return null;
    });

    /** Idle-paint metric — the first visible metric. Used by the pre-
     *  engine LCP placeholder so the hero pill paints the same shape
     *  as the active branch. */
    const idlePlaceholder = computed<MetricDef | null>(
        () => registry.visible.value[0] ?? null,
    );

    /** The state-machine projection — exhaust every (engineState ×
     *  active-metric-state) combination explicitly so adding a future
     *  branch is a compile-time addition here, not an ad-hoc v-if
     *  drift.
     *
     *  The dots-vs-value distinction reads the active metric's
     *  `TestState`: `started` (the engine has flagged the phase
     *  but hasn't emitted the first sample) → no-data; `active` (the
     *  first sample has landed; the engine is sampling) → with-data.
     *  `hasData` from the bundle is an OR-companion — it catches the
     *  case where the result-bridge has produced a non-null value
     *  even when the lifecycle hasn't transitioned to `active` yet
     *  (e.g. final-snapshot replay on remount). */
    const variant = computed<HeroVariant>(() => {
        const state = engineState.value;

        if (state === "complete") {
            return "complete";
        }

        if (state === "running") {
            const metric = activeMetric.value;
            if (!metric) return "running-no-data";
            const metricState = results[metric.id].state.value;
            // `active` or `finished` (the engine has moved past this
            // phase but we still read the trailing value) both carry
            // real data. `started` is the brief window between phase
            // assignment and the first emitted sample.
            if (
                metricState === TestState.active
                || metricState === TestState.finished
            ) {
                return "running-with-data";
            }
            return "running-no-data";
        }

        // `state === "idle"` — pre-engine or post-abort. The
        // subscription machine clears `results` on abort, so the
        // observable surface is identical to a fresh idle; we
        // disambiguate via the per-metric state machine: if any
        // metric was ever in flight (state > pending) and we landed
        // back at idle without `allComplete`, the prior run was
        // aborted. The visible-metric set is the variant-aware
        // source, so dpi / default both compute correctly.
        const anyTouched = registry.visible.value.some(
            (m) => results[m.id].state.value !== TestState.notStarted,
        );
        if (anyTouched) {
            return "aborted";
        }
        return "idle";
    });

    return {
        variant,
        engineState,
        activeMetric,
        idlePlaceholder,
        results,
        isIdle: computed(() => variant.value === "idle"),
        isRunningNoData: computed(() => variant.value === "running-no-data"),
        isRunningWithData: computed(() => variant.value === "running-with-data"),
        isComplete: computed(() => variant.value === "complete"),
        isAborted: computed(() => variant.value === "aborted"),
    };
}
